/**
 * Global Main Clue / Sponsor Run question bank + per-question usage history.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { fetchSponsorOrganizationsByIds } from './sponsorQueries';

export const MAIN_CLUE_QUESTIONS = 'mainClueQuestions';
export const MAIN_CLUE_USAGE_SUB = 'usage';

function usageDocId(eventId, sponsorId, questionId) {
  const e = (eventId || '').toString().trim();
  const s = (sponsorId || '').toString().trim();
  const q = (questionId || '').toString().trim();
  return `${e}__${s}__${q}`.replace(/[/]/g, '_');
}

export async function listMainClueQuestions() {
  const snap = await getDocs(collection(firestore, MAIN_CLUE_QUESTIONS));
  const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  list.sort((a, b) =>
    (a.prompt || '').toString().localeCompare((b.prompt || '').toString(), undefined, { sensitivity: 'base' })
  );
  return list;
}

export async function getMainClueQuestion(questionId) {
  const id = (questionId || '').toString().trim();
  if (!id) return null;
  const snap = await getDoc(doc(firestore, MAIN_CLUE_QUESTIONS, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * @param {{ prompt: string, answer?: string, tags?: string[], notes?: string }} data
 */
export async function createMainClueQuestion(data) {
  const ref = doc(collection(firestore, MAIN_CLUE_QUESTIONS));
  const prompt = (data.prompt || '').toString().trim();
  if (!prompt) throw new Error('Question prompt is required');
  const tags = Array.isArray(data.tags)
    ? data.tags.map((t) => String(t || '').trim()).filter(Boolean)
    : [];
  await setDoc(ref, {
    prompt,
    answer: (data.answer || '').toString().trim() || null,
    tags,
    notes: (data.notes || '').toString().trim() || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateMainClueQuestion(questionId, data) {
  const id = (questionId || '').toString().trim();
  if (!id) throw new Error('Missing question id');
  const payload = { updatedAt: serverTimestamp() };
  if (data.prompt !== undefined) {
    const p = (data.prompt || '').toString().trim();
    if (!p) throw new Error('Question prompt is required');
    payload.prompt = p;
  }
  if (data.answer !== undefined) {
    payload.answer = (data.answer || '').toString().trim() || null;
  }
  if (data.tags !== undefined) {
    payload.tags = Array.isArray(data.tags)
      ? data.tags.map((t) => String(t || '').trim()).filter(Boolean)
      : [];
  }
  if (data.notes !== undefined) {
    payload.notes = (data.notes || '').toString().trim() || null;
  }
  await setDoc(doc(firestore, MAIN_CLUE_QUESTIONS, id), payload, { merge: true });
}

export async function deleteMainClueQuestion(questionId) {
  const id = (questionId || '').toString().trim();
  if (!id) return;
  const usageCol = collection(firestore, MAIN_CLUE_QUESTIONS, id, MAIN_CLUE_USAGE_SUB);
  const usageSnap = await getDocs(usageCol);
  await Promise.all(usageSnap.docs.map((u) => deleteDoc(u.ref)));
  await deleteDoc(doc(firestore, MAIN_CLUE_QUESTIONS, id));
}

export async function listQuestionUsage(questionId) {
  const id = (questionId || '').toString().trim();
  if (!id) return [];
  const usageCol = collection(firestore, MAIN_CLUE_QUESTIONS, id, MAIN_CLUE_USAGE_SUB);
  let snap;
  try {
    snap = await getDocs(query(usageCol, orderBy('usedAt', 'desc')));
  } catch {
    snap = await getDocs(usageCol);
  }
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function usageRowTime(u) {
  const v = u && u.usedAt;
  if (v && typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v === 'number') return v;
  return 0;
}

/**
 * Recompute denormalized sponsor labels from usage rows + sponsorOrganizations directory.
 * Stored on the question doc as `usedBySponsors` for search and display.
 */
export async function rebuildQuestionUsedBySponsors(questionId) {
  const qid = (questionId || '').toString().trim();
  if (!qid) return;
  const rows = await listQuestionUsage(qid);
  const byKey = new Map();
  const historyGroups = new Map();
  for (const u of rows) {
    const orgId = (u.organizationId || '').toString().trim() || null;
    const partId = (u.sponsorParticipationId || '').toString().trim();
    if (!partId) continue;
    const key = orgId || `part:${partId}`;
    const t = usageRowTime(u);
    const prev = byKey.get(key);
    if (!prev || t >= prev.t) {
      byKey.set(key, {
        t,
        organizationId: orgId,
        sponsorParticipationId: partId,
        sponsorNameSnapshot: (u.sponsorNameSnapshot || '').toString().trim() || null,
      });
    }
    const eid = (u.eventId || '').toString().trim();
    if (!eid) continue;
    let g = historyGroups.get(key);
    if (!g) {
      g = { organizationId: orgId, sponsorParticipationId: partId, byEvent: new Map() };
      historyGroups.set(key, g);
    }
    const setId = u.setId === 'A' || u.setId === 'B' || u.setId === 'C' ? u.setId : null;
    const eventYear = u.eventYear != null ? Number(u.eventYear) : null;
    const prevEv = g.byEvent.get(eid);
    if (!prevEv || t >= prevEv.t) {
      g.byEvent.set(eid, { t, eventYear: Number.isFinite(eventYear) ? eventYear : null, setId, eventId: eid });
    }
  }
  const orgIds = [...new Set([...byKey.values()].map((x) => x.organizationId).filter(Boolean))];
  const orgById = await fetchSponsorOrganizationsByIds(orgIds);
  const usedBySponsors = [...byKey.values()]
    .map((row) => {
      const org = row.organizationId ? orgById.get(row.organizationId) : null;
      const directoryName = (org?.displayName || org?.name || '').toString().trim();
      const name =
        directoryName ||
        row.sponsorNameSnapshot ||
        row.sponsorParticipationId;
      return {
        organizationId: row.organizationId,
        sponsorParticipationId: row.sponsorParticipationId,
        name,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const sponsorUsageHistory = [...historyGroups.entries()]
    .map(([key, g]) => {
      const latest = byKey.get(key);
      const org = g.organizationId ? orgById.get(g.organizationId) : null;
      const directoryName = (org?.displayName || org?.name || '').toString().trim();
      const name =
        directoryName ||
        latest?.sponsorNameSnapshot ||
        g.sponsorParticipationId;
      const uses = [...g.byEvent.values()]
        .map(({ eventYear, setId, eventId }) => ({ eventYear, setId, eventId }))
        .sort((a, b) => {
          const ya = a.eventYear != null ? a.eventYear : -999999;
          const yb = b.eventYear != null ? b.eventYear : -999999;
          if (yb !== ya) return yb - ya;
          return (a.eventId || '').localeCompare(b.eventId || '', undefined, { sensitivity: 'base' });
        });
      return {
        organizationId: g.organizationId,
        sponsorParticipationId: g.sponsorParticipationId,
        name,
        uses,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  await setDoc(
    doc(firestore, MAIN_CLUE_QUESTIONS, qid),
    { usedBySponsors, sponsorUsageHistory },
    { merge: true }
  );
}

/**
 * Record or refresh that this question was used for an event/sponsor/set (idempotent doc id).
 * @param {{ skipSponsorAggregate?: boolean }} [opts] If true, caller must run `rebuildQuestionUsedBySponsors` (e.g. batch saves).
 */
export async function upsertQuestionUsage(questionId, payload, opts = {}) {
  const qid = (questionId || '').toString().trim();
  if (!qid) return;
  const {
    eventId,
    eventYear,
    sponsorParticipationId,
    organizationId,
    sponsorNameSnapshot,
    setId,
  } = payload;
  const eid = (eventId || '').toString().trim();
  const sid = (sponsorParticipationId || '').toString().trim();
  if (!eid || !sid) return;
  const uid = usageDocId(eid, sid, qid);
  const usageRef = doc(firestore, MAIN_CLUE_QUESTIONS, qid, MAIN_CLUE_USAGE_SUB, uid);
  await setDoc(
    usageRef,
    {
      eventId: eid,
      eventYear: eventYear != null ? Number(eventYear) : null,
      sponsorParticipationId: sid,
      organizationId: (organizationId || '').toString().trim() || null,
      sponsorNameSnapshot: (sponsorNameSnapshot || '').toString().trim() || null,
      setId: setId === 'A' || setId === 'B' || setId === 'C' ? setId : null,
      usedAt: serverTimestamp(),
    },
    { merge: true }
  );
  if (!opts.skipSponsorAggregate) {
    await rebuildQuestionUsedBySponsors(qid);
  }
}

/**
 * After saving a sponsor's question list, sync usage rows for that sponsor.
 */
export async function syncUsageForSponsorQuestions(eventId, sponsor, setId, questionIds, eventYear = null) {
  const eid = (eventId || '').toString().trim();
  if (!eid || !sponsor) return;
  const sid = sponsor.id;
  const orgId = sponsor.organizationId || null;
  const name = sponsor.name || sponsor.companyName || '';
  const year = eventYear != null ? Number(eventYear) : null;
  const touched = new Set();
  for (const qid of questionIds || []) {
    if (!qid) continue;
    await upsertQuestionUsage(
      qid,
      {
        eventId: eid,
        eventYear: year,
        sponsorParticipationId: sid,
        organizationId: orgId,
        sponsorNameSnapshot: name,
        setId,
      },
      { skipSponsorAggregate: true }
    );
    touched.add(qid);
  }
  await Promise.all([...touched].map((id) => rebuildQuestionUsedBySponsors(id)));
}
